import { Injectable } from '@angular/core';
import { LoggerService } from '../shared/logger/logger.service';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { GoogleProviderService } from '../shared/google-provider.service';

/**
 * Service class for fetching weather data. Requests to https://openweathermap.org/api are proxied via QM backend
 * because Open Weather Map API doesn't support CORS :(
 */
@Injectable()
export class WeatherService {

  /**
   * Default constructor
   * @param {HttpClient} http - refined http service, automatically adds authentication token
   * @param {LoggerService} logger - logger service
   */
  constructor(private http: HttpClient, private logger: LoggerService) { }

  /**
   * Get weather for city & country
   * @param {string} city
   * @param {string} country
   * @returns {Observable<any>}
   */
  public getWeatherForCity(city: string, country: string): Promise<any> {
    return this.http.get<any[]>(environment.regoApiURL + 'weather/current?city=' + city + '&country=' + country).toPromise();
  }

  /**
   * Get weather for WGS-84 point
   * @param {string} latitude
   * @param {string} longitude
   * @returns {Promise<any>}
   */
  public getWeatherForLocation(latitude?: string, longitude?: string): Promise<any> {
    return this.getWeatherData('current', latitude, longitude);
  }

  /**
   *  Get forecast for city & country
   * @param {string} city
   * @param {string} country
   * @returns {Observable<any>}
   */
  public getForecastForCity(city: string, country: string): Promise<any> {
    return this.http.get<any[]>(environment.regoApiURL + 'weather/forecast?city=' + city + '&country=' + country).toPromise();
  }

  /**
   * Get forecast for WGS-84 point
   * @param {string} latitude
   * @param {string} longitude
   * @returns {Promise<any>}
   */
  public getForecastForLocation(latitude?: string, longitude?: string): Promise<any> {
    return this.getWeatherData('forecast', latitude, longitude);
  }

  /**
   * Fetch current location. Browser geolocation is primary option, Google location api is secondary.
   * @returns {Promise<any>}
   */
  public getLocation(): Promise<any> {
    if (navigator.geolocation) {
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          maximumAge: 50000,
          timeout: 40000,
          enableHighAccuracy: true
        });
      });
    } else {
      return this.tryAPIGeolocation();
    }
  }

  /**
   * Get weather data from API. Fetch coordinates via getLocation of tryAPIGeolocation if none are provided.
   * @param {"forecast" | "current"} type
   * @param {string} latitude
   * @param {string} longitude
   * @returns {Promise<any>}
   */
  private getWeatherData(type: 'forecast' | 'current', latitude?: string, longitude?: string) {
    return new Promise((resolve, reject) => {
      if (!latitude) {
        this.getLocation().then(data => {
          console.log(data);
          this.http.get<any[]>(environment.regoApiURL + 'weather/' + type + '?latitude='
            + data.coords.latitude + '&longitude=' + data.coords.longitude).subscribe(weatherData => {
            resolve(weatherData);
          });
        }).catch(error => {
          console.error(error);
          this.tryAPIGeolocation().then(data => {
            console.log(data);
            this.http.get<any[]>(environment.regoApiURL + 'weather/' + type + '?latitude='
              + data.location.lat + '&longitude=' + data.location.lng).subscribe(weatherData => {
              resolve(weatherData);
            });
          }).catch(err => {
            console.error(err);
            reject(err);
          });
        });
      } else {
        this.http.get<any[]>(environment.regoApiURL + 'weather/' + type + '?latitude='
          + latitude + '&longitude=' + longitude).subscribe(weatherData => {
          resolve(weatherData);
        });
      }
    });
  }

  private tryAPIGeolocation(): Promise<any> {
    return this.http.post('https://www.googleapis.com/geolocation/v1/geolocate?key=' + GoogleProviderService.key, {}).toPromise();
  }
}
